import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ClipboardList, Search, Filter, Calendar, User, RefreshCw,
  CheckCircle, XCircle, Briefcase, UserCog, DollarSign, Crown, Trash2, Plus, Edit,
  MapPin, ExternalLink,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Keys we render with custom UI (mini map etc.) — don't dump them as raw text
// in the generic details block.
const CUSTOM_RENDERED_DETAIL_KEYS = new Set(['marks', 'marks_truncated']);

const AdminAuditLogs = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionTypes, setActionTypes] = useState([]);
  
  // Filters
  const [selectedActionType, setSelectedActionType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchLogs();
    fetchActionTypes();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      
      if (selectedActionType && selectedActionType !== 'all') {
        params.append('action_type', selectedActionType);
      }
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      const queryString = params.toString();
      const url = `${API}/admin/audit-logs${queryString ? '?' + queryString : ''}`;
      
      const response = await axios.get(url, getAuthHeader());
      setLogs(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/action-types`, getAuthHeader());
      setActionTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch action types:', error);
    }
  };

  const handleFilter = () => {
    fetchLogs();
  };

  const handleClearFilters = () => {
    setSelectedActionType('all');
    setStartDate('');
    setEndDate('');
    // Re-fetch with cleared filters
    setTimeout(() => fetchLogs(), 100);
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'user_approved':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'user_denied':
        return <XCircle size={16} className="text-red-500" />;
      case 'pro_status_changed':
        return <Crown size={16} className="text-yellow-500" />;
      case 'job_created':
        return <Plus size={16} className="text-blue-500" />;
      case 'job_deleted':
        return <Trash2 size={16} className="text-red-500" />;
      case 'job_approved':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'staff_created':
        return <UserCog size={16} className="text-purple-500" />;
      case 'staff_deleted':
        return <Trash2 size={16} className="text-red-500" />;
      case 'payout_sent':
        return <DollarSign size={16} className="text-green-500" />;
      case 'admin_marked_houses':
        return <MapPin size={16} className="text-purple-500" />;
      default:
        return <Edit size={16} className="text-gray-500" />;
    }
  };

  const getActionBadge = (actionType) => {
    const badges = {
      'user_approved': { label: 'User Approved', className: 'bg-green-100 text-green-700' },
      'user_denied': { label: 'User Denied', className: 'bg-red-100 text-red-700' },
      'pro_status_changed': { label: 'Pro Status Changed', className: 'bg-yellow-100 text-yellow-700' },
      'job_created': { label: 'Job Created', className: 'bg-blue-100 text-blue-700' },
      'job_deleted': { label: 'Job Deleted', className: 'bg-red-100 text-red-700' },
      'job_approved': { label: 'Job Approved', className: 'bg-green-100 text-green-700' },
      'staff_created': { label: 'Staff Created', className: 'bg-purple-100 text-purple-700' },
      'staff_deleted': { label: 'Staff Deleted', className: 'bg-red-100 text-red-700' },
      'payout_sent': { label: 'Payout Sent', className: 'bg-emerald-100 text-emerald-700' },
      'admin_marked_houses': { label: 'Houses Marked', className: 'bg-purple-100 text-purple-700' },
    };
    
    const badge = badges[actionType] || { label: actionType.replace(/_/g, ' '), className: 'bg-gray-100 text-gray-700' };
    return <Badge className={badge.className}>{badge.label}</Badge>;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-700">Admin</Badge>;
      case 'manager':
        return <Badge className="bg-blue-100 text-blue-700">Manager</Badge>;
      case 'auditor':
        return <Badge className="bg-green-100 text-green-700">Auditor</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{role}</Badge>;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatDetails = (details) => {
    if (!details || Object.keys(details).length === 0) return null;

    return Object.entries(details)
      .filter(([key]) => !CUSTOM_RENDERED_DETAIL_KEYS.has(key))
      .map(([key, value]) => (
        <span key={key} className="inline-flex items-center gap-1 mr-3 text-xs text-gray-500">
          <span className="font-medium">{key.replace(/_/g, ' ')}:</span>
          <span>{typeof value === 'number' ? `$${value.toFixed(2)}` : String(value)}</span>
        </span>
      ));
  };

  // Build a Google Static Maps URL with red dots at every mark position.
  // Static Maps caps URL length at ~8 KB, so we cap at 50 markers per preview
  // (Google itself recommends ~50 to keep size reasonable). Returns null if
  // we have no usable coords or no API key.
  const buildMarksMapUrl = (marks) => {
    if (!GOOGLE_MAPS_KEY || !Array.isArray(marks) || marks.length === 0) return null;
    const sample = marks
      .filter((m) => typeof m?.lat === 'number' && typeof m?.lng === 'number')
      .slice(0, 50);
    if (sample.length === 0) return null;
    const markerParams = sample
      .map((m) => `${m.lat.toFixed(5)},${m.lng.toFixed(5)}`)
      .join('|');
    const url = new URL('https://maps.googleapis.com/maps/api/staticmap');
    url.searchParams.set('size', '320x140');
    url.searchParams.set('scale', '2');
    url.searchParams.set('maptype', 'roadmap');
    url.searchParams.set('markers', `color:purple|size:tiny|${markerParams}`);
    url.searchParams.set('key', GOOGLE_MAPS_KEY);
    return url.toString();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList size={28} className="text-purple-600" />
            Audit Logs
          </h1>
          <p className="text-gray-500">Track all staff actions and changes</p>
        </div>
        <Button
          onClick={fetchLogs}
          variant="outline"
          className="flex items-center gap-2"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Label className="mb-2 block">Action Type</Label>
            <Select
              value={selectedActionType}
              onValueChange={setSelectedActionType}
            >
              <SelectTrigger data-testid="action-type-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="min-w-[150px]">
            <Label className="mb-2 block">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="start-date-filter"
            />
          </div>
          
          <div className="min-w-[150px]">
            <Label className="mb-2 block">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="end-date-filter"
            />
          </div>
          
          <Button
            onClick={handleFilter}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="apply-filter-btn"
          >
            <Filter size={16} className="mr-2" />
            Apply Filters
          </Button>
          
          <Button
            onClick={handleClearFilters}
            variant="outline"
            data-testid="clear-filter-btn"
          >
            Clear
          </Button>
        </div>
      </Card>

      {/* Logs Table */}
      {logs.length === 0 ? (
        <Card className="p-12 text-center">
          <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No audit logs found</p>
          <p className="text-sm text-gray-400 mt-1">
            Actions will be logged here when staff members make changes
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {logs.map((log) => (
            <Card 
              key={log.id} 
              className="p-4 hover:shadow-md transition-shadow"
              data-testid={`audit-log-${log.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {getActionIcon(log.action_type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {getActionBadge(log.action_type)}
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600 flex items-center gap-1">
                      <User size={14} />
                      {log.performed_by.name}
                    </span>
                    {getRoleBadge(log.performed_by.role)}
                  </div>
                  
                  <p className="text-gray-900">
                    <span className="font-medium">{log.target.type}:</span>{' '}
                    {log.target.name}
                  </p>
                  
                  {log.details && Object.keys(log.details).length > 0 && (
                    <div className="mt-1">
                      {formatDetails(log.details)}
                    </div>
                  )}

                  {/* Custom block for admin_marked_houses entries: mini map
                      preview of every marker placed in that batch + a quick
                      link to the Admin Mark page for the targeted job. */}
                  {log.action_type === 'admin_marked_houses' && (
                    <div className="mt-3 flex flex-wrap items-start gap-3" data-testid={`admin-mark-detail-${log.id}`}>
                      {(() => {
                        const url = buildMarksMapUrl(log.details?.marks);
                        if (!url) return null;
                        return (
                          <img
                            src={url}
                            alt={`${log.details?.marks_added || 0} marks preview`}
                            className="rounded-md border border-purple-200 shadow-sm"
                            width={320}
                            height={140}
                            loading="lazy"
                          />
                        );
                      })()}
                      <div className="flex flex-col gap-1.5">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-purple-700">{log.details?.marks_added || 0}</span>{' '}
                          house{(log.details?.marks_added || 0) === 1 ? '' : 's'} credited to{' '}
                          <span className="font-semibold">{log.details?.credited_user_name || 'user'}</span>
                          {log.details?.marks_truncated && (
                            <span className="ml-1 text-xs text-amber-600">
                              (preview shows first 50 only)
                            </span>
                          )}
                        </p>
                        {log.target?.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-purple-700 border-purple-200 hover:bg-purple-50 self-start"
                            onClick={() => navigate(`/admin/jobs/${log.target.id}/mark`)}
                            data-testid={`audit-open-mark-${log.id}`}
                          >
                            <ExternalLink size={14} className="mr-1" />
                            Open in Admin Mark
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="text-right flex-shrink-0">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar size={14} />
                    {formatTimestamp(log.timestamp)}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Summary */}
      <div className="mt-6 text-center text-sm text-gray-500">
        Showing {logs.length} log entries
      </div>
    </div>
  );
};

export default AdminAuditLogs;
